


























































import { Component, Prop, Vue } from "vue-property-decorator";
import axios from "axios";
import { Analysis } from "@/api/analyses/types";
import { FarmAnalysisResult } from "@/api/results/types";
import ImagesTab from "@/components/wrappedGoogleMaps/image-plotter/ImagesTab.vue";
import AgroOnOff from "@/components/agroOnOff/AgroOnOff.vue";

@Component({
  name: "CropDrawer",
  components: {
    AgroOnOff,
    ImageTab: ImagesTab,
  },
})
export default class CropDrawer extends Vue {
  public map: google.maps.Map | null = null;
  private polygons: google.maps.Polygon[] = [];
  private geoJsonFeatures: google.maps.Data.Feature[] = [];
  private selectedPolygon: google.maps.Polygon | null = null;
  private drawingManager: google.maps.drawing.DrawingManager | null = null;
  public geoJsonVisible: boolean = true;
  public allYears: boolean = false;

  @Prop({ type: Object, required: false }) public analysis?: Analysis;
  @Prop({ type: Object, required: false }) public farmAnalysisResult?: FarmAnalysisResult;
  @Prop({ type: String, required: false }) public farmAnalysisUuid?: string;

  mounted() {
    this.initializeMap();
    this.loadGeoJsonFromS3();
    this.initializeDrawingManager();
    document.addEventListener('keydown', this.handleKeyDown);
  }

  beforeDestroy() {
    document.removeEventListener('keydown', this.handleKeyDown);
  }

  private handleKeyDown(event: KeyboardEvent) {
    if (event.key === 'Escape' && this.drawingManager) {
      this.drawingManager.setDrawingMode(null);
      this.removeIncompleteOverlay();
      this.drawingManager.setDrawingMode(google.maps.drawing.OverlayType.POLYGON);
    }
    if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === 'z') {
      event.preventDefault();
      this.undoLastPolygon();
    }
  }

  private undoLastPolygon() {
    if (this.polygons.length > 0) {
      const lastPolygon = this.polygons.pop();
      if (lastPolygon) {
        lastPolygon.setMap(null);
        this.updatePolygonColors();
      }
    }
  }

  private removeIncompleteOverlay() {
    const overlay = (this.drawingManager as any).overlay;

    if (overlay) {
      overlay.setMap(null);
    }
  }

  private initializeMap() {
    const mapElement = this.$refs.map as HTMLElement;
    this.map = new google.maps.Map(mapElement, {
      center: { lat: 0, lng: 0 },
      zoom: 2,
      mapTypeId: 'satellite',
    });
  }

  private toggleGeoJsonVisibility() {
    if (this.geoJsonVisible) {
      this.map?.data.setMap(null);
    } else {
      this.map?.data.setMap(this.map);
    }
    this.geoJsonVisible = !this.geoJsonVisible;
  }

  private async loadGeoJsonFromS3() {
    try {
      const response = await axios.get(this.farmAnalysisResult?.s3GeoJsonUrl ?? "");
      const geoJsonData = response.data;
      this.addGeoJsonToMap(geoJsonData);
    } catch (error) {
      console.error("Erro ao carregar o GeoJSON do S3:", error);
    }
  }

  private addGeoJsonToMap(geoJsonData: any) {
    if (this.map) {
      this.geoJsonFeatures = this.map.data.addGeoJson(geoJsonData);
      this.adjustMapBounds();
      this.applyGeoJsonStyles();
      this.map.data.setMap(this.map);
    }
  }

  private adjustMapBounds() {
    const bounds = new google.maps.LatLngBounds();
    this.map?.data.forEach((feature) => {
      feature.setProperty('originalFillColor', feature.getProperty('fill') || '#FF0000');
      this.processPoints(feature.getGeometry(), bounds.extend, bounds);
    });
    this.map?.fitBounds(bounds);
  }

  private applyGeoJsonStyles() {
    this.map?.data.setStyle((feature) => {
      const fill = feature.getProperty('originalFillColor');
      const type = feature.getProperty('type') || 'OTHER';

      return {
        fillColor: fill,
        strokeWeight: type === 'CROP' ? 2 : 2,
        strokeColor: type === 'CROP' ? '#FFF' : '#000',
        fillOpacity: type === 'CROP' ? 0.3 : 0,
      };
    });
  }

  private initializeDrawingManager() {
    if (!this.map) return;
    this.drawingManager = new google.maps.drawing.DrawingManager({
      drawingMode: google.maps.drawing.OverlayType.POLYGON,
      drawingControl: true,
      drawingControlOptions: {
        position: google.maps.ControlPosition.TOP_CENTER,
      },
      polygonOptions: {
        fillColor: "#ff47dd",
        strokeColor: "#ff47dd",
        fillOpacity: 0.5,
        strokeWeight: 4,
      },
    });

    this.drawingManager.setMap(this.map);
    google.maps.event.addListener(this.drawingManager, "polygoncomplete", this.handlePolygonComplete);
  }

  private handlePolygonComplete(polygon: google.maps.Polygon) {
    if (polygon.getPath().getLength() < 3) {
      polygon.setMap(null);
      return;
    }

    this.polygons.push(polygon);
    this.updatePolygonColors();
  }

  private handleFileUpload(event: Event) {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files[0]) {
      const file = input.files[0];
      const reader = new FileReader();

      reader.onload = (e) => {
        try {
          const content = e.target?.result as string;
          const geoJsonData = JSON.parse(content);

          this.importGeoJson(geoJsonData);
        } catch (error) {
          console.error("Erro ao ler o arquivo GeoJSON:", error);
        }
      };

      reader.readAsText(file);
    }
  }

  private importGeoJson(geoJsonData: any) {
    this.clearDrawnPolygons();

    const cropFeatures = geoJsonData.features.filter(
      (feature: any) => feature.properties?.type === "CROP"
    );

    cropFeatures.forEach((feature: any) => {
      const coordinates = feature.geometry.coordinates[0];
      const paths = coordinates.map((coord: [number, number]) => {
        return { lat: coord[1], lng: coord[0] };
      });

      const polygon = new google.maps.Polygon({
        paths: paths,
        fillColor: "#ff47dd",
        strokeColor: "#ff47dd",
        fillOpacity: 0.5,
        strokeWeight: 4,
      });

      polygon.setMap(this.map);
      this.polygons.push(polygon);
    });
    this.updatePolygonColors();
    this.fitMapToPolygons();
  }

  private fitMapToPolygons() {
    if (!this.map) return;

    const bounds = new google.maps.LatLngBounds();
    this.polygons.forEach((polygon) => {
      polygon.getPath().forEach((latLng) => {
        bounds.extend(latLng);
      });
    });

    this.map.fitBounds(bounds);
  }


  private clearDrawnPolygons() {
    this.polygons.forEach((polygon) => {
      polygon.setMap(null);
    });
    this.polygons = [];
  }

  private removePolygon(polygon: google.maps.Polygon) {
    polygon.setMap(null);
    const index = this.polygons.indexOf(polygon);
    if (index > -1) {
      this.polygons.splice(index, 1);
    }
    if (this.selectedPolygon === polygon) {
      this.selectedPolygon = null;
    }

    this.updatePolygonColors();
  }

  private updatePolygonColors() {
    this.restoreOriginalPolygonColors();

    this.polygons.forEach((polygon: any) => {
      const newPolygonPath = polygon.getPath().getArray();

      this.geoJsonFeatures.forEach((feature: any) => {
        const featureGeometry = feature.getGeometry();
        if (featureGeometry?.getType() === "Polygon") {
          const paths = featureGeometry.getArray();
          paths.forEach((path: google.maps.Data.LinearRing) => {
            const featurePathArray = path.getArray();
            if (this.checkPolygonIntersection(newPolygonPath, featurePathArray)) {
              if (feature.getProperty("type") === "CROP") {
                this.map?.data.overrideStyle(feature, { fillColor: "#FFF", strokeColor: "#FFF" });
              }
            }
          });
        }
      });
    });
  }

  private restoreOriginalPolygonColors() {
    this.geoJsonFeatures.forEach((feature) => {
      this.map?.data.overrideStyle(feature, {
        fillColor: feature.getProperty("originalFillColor"),
      });
    });
  }

  private checkPolygonIntersection(polygon1: google.maps.LatLng[], polygon2: google.maps.LatLng[]): boolean {
    return polygon1.some((point) => google.maps.geometry.poly.containsLocation(point, new google.maps.Polygon({ paths: polygon2 }))) ||
      polygon2.some((point) => google.maps.geometry.poly.containsLocation(point, new google.maps.Polygon({ paths: polygon1 })));
  }

  private selectPolygon(polygon: google.maps.Polygon) {
    this.selectedPolygon?.setOptions({ strokeColor: "#000000" });
    polygon.setOptions({ strokeColor: "#FF0000" });
    this.selectedPolygon = polygon;
  }

  private deletePolygon(index: number) {
    const polygon = this.polygons[index];
    this.removePolygon(polygon);
  }

  private getPolygonArea(polygon: google.maps.Polygon): number {
    const path = polygon.getPath().getArray();
    return Math.round(google.maps.geometry.spherical.computeArea(path) / 10000);
  }

  private processPoints(geometry: google.maps.Data.Geometry, callback: Function, thisArg: any) {
    const type = geometry.getType();

    switch (type) {
      case 'Point':
        callback.call(thisArg, (geometry as google.maps.Data.Point).get());
        break;

      case 'MultiPoint':
      case 'LineString':
      case 'LinearRing':
        (geometry as google.maps.Data.GeometryCollection).getArray().forEach((point) => callback.call(thisArg, point));
        break;

      case 'Polygon':
      case 'MultiLineString':
        (geometry as google.maps.Data.GeometryCollection).getArray().forEach((path) => {
          this.processPoints(path, callback, thisArg);
        });
        break;

      case 'MultiPolygon':
        (geometry as google.maps.Data.GeometryCollection).getArray().forEach((polygon: any) => {
          polygon.getArray().forEach((path: any) => this.processPoints(path, callback, thisArg));
        });
        break;

      default:
        console.warn(`Unsupported geometry type: ${type}`);
    }
  }
  private sendData() {
    const polygons = this.polygons.map((polygon) => {
      const coordinates = polygon.getPath().getArray().map(({ lat, lng }) => [lng(), lat()]);

      if (coordinates[0] !== coordinates[coordinates.length - 1]) {
        coordinates.push(coordinates[0]);
      }
      
      return {
        "ids": [],
        "uuids": [],
        "labels": [],
        "geom": {
          type: "Polygon",
          coordinates: [coordinates],
        }
      }
    });

    this.$emit("crop-drawer:update-polygons", {
      polygons, allYears: this.allYears
    });
  }


}
